/* Order Health Board — 4 tiles (duplicates, no tracking, missing Second Name, invoices waiting 2+ days), one-click Fix. */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, AlertTriangle, Clock, Loader2, PackageX, Tag } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const TILES = [
  { k: "dup", icon: AlertTriangle, label: "Possible duplicates", tone: "rose", count: (h) => h.dup_pending,
    sub: (h) => (h.dup_high_risk ? `${h.dup_high_risk} high risk` : "parcels to confirm") },
  { k: "no_tracking", icon: PackageX, label: "No tracking", tone: "amber", count: (h) => h.no_tracking, sub: () => "open rows" },
  { k: "no_second_name", icon: Tag, label: "Missing Second Name", tone: "violet", count: (h) => h.no_second_name, sub: () => "open rows" },
  { k: "inv_waiting", icon: Clock, label: "Invoices waiting 2+ days", tone: "sky", count: (h) => h.inv_waiting,
    sub: (h) => `${h.inv_waiting_rows} rows${h.inv_waiting_keys?.length ? ` · ${h.inv_waiting_keys.slice(0, 4).join(", ")}${h.inv_waiting_keys.length > 4 ? "…" : ""}` : ""}` },
];
const TONE = {
  rose: "border-rose-200 bg-rose-50 text-rose-800", amber: "border-amber-200 bg-amber-50 text-amber-800",
  violet: "border-violet-200 bg-violet-50 text-violet-800", sky: "border-sky-200 bg-sky-50 text-sky-800",
};

export const OrderHealthBoard = ({ onFix }) => {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(null);
  const load = async () => {
    setH(null);
    try { setH((await axios.get(`${API}/order-register/health`, { headers: getAuthHeader() })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load order health"); setOpen(false); }
  };
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("health") === "1") { setOpen(true); load(); }
  }, []);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="or-health-btn"
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-emerald-400 bg-white px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors">
          <Activity className="w-3.5 h-3.5" /> Health
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(560px,92vw)] p-3" align="start" data-testid="or-health-panel">
        <div className="text-xs font-bold text-slate-700 mb-2">Order Health — open orders (not completed, not cancelled)</div>
        {!h ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {TILES.map(({ k, icon: Icon, label, tone, count, sub }) => (
              <div key={k} className={`rounded-lg border p-2.5 ${TONE[tone]}`} data-testid={`or-health-tile-${k}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold"><Icon className="w-3.5 h-3.5" /> {label}</div>
                <div className="mt-1 flex items-end justify-between gap-2">
                  <div>
                    <div className="text-2xl font-bold leading-none" data-testid={`or-health-count-${k}`}>{count(h)}</div>
                    <div className="text-[10px] opacity-80 mt-0.5 truncate max-w-[170px]" title={sub(h)}>{sub(h)}</div>
                  </div>
                  <button type="button" disabled={!count(h)} onClick={() => { setOpen(false); onFix(k); }}
                    className="rounded-md bg-slate-900 hover:bg-slate-700 disabled:opacity-40 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors"
                    data-testid={`or-health-fix-${k}`}>Fix</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
